import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchContent } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const categoryTabs = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "series", label: "电视剧" },
  { value: "review", label: "影评" },
];

const hotKeywords = ["暗夜追光", "迷雾之城", "科幻", "悬疑", "陈亦然"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const category = searchParams.get("category") || "all";
  const [input, setInput] = useState(keyword);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 疯狂的欲望` : "搜索影视 - 疯狂的欲望",
    "在疯狂的欲望搜索影视名称、演员、导演或影评关键词，快速找到想看的电影与电视剧。",
  );

  const results = useMemo(() => searchContent(keyword, category), [keyword, category]);

  const submit = (value: string, cat = category) => {
    const params: Record<string, string> = {};
    if (value.trim()) params.q = value.trim();
    if (cat !== "all") params.category = cat;
    setSearchParams(params);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-14">
          <nav className="mb-4 flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">搜索影视内容</h1>
          <p className="mt-2 text-sm text-foreground-600">支持按影视名称、演员、导演与影评关键词检索。</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="mt-6 flex max-w-2xl flex-col gap-2 sm:flex-row"
          >
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder="输入关键词，如：暗夜追光、陈亦然、悬疑"
                className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-500 focus:border-primary-400"
              />
            </div>
            <button type="submit" className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700">
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>热门搜索：</span>
            {hotKeywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { setInput(k); submit(k); }}
                className="cursor-pointer rounded-full bg-background-200 px-3 py-1 text-foreground-700 transition-colors hover:bg-secondary-200 hover:text-secondary-900"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
        {keyword && (
          <div className="mb-6 flex flex-col gap-4">
            <p className="text-sm text-foreground-600">
              关键词 <span className="font-semibold text-foreground-950">“{keyword}”</span> 共找到{" "}
              <span className="font-semibold text-accent-700">{results.length}</span> 条结果
            </p>
            <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => submit(keyword, tab.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    category === tab.value ? "bg-primary-600 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!keyword ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-400" />
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">开始你的搜索</h3>
            <p className="mt-2 text-sm text-foreground-600">输入影视名称或关键词，也可以试试上方的热门搜索。</p>
          </div>
        ) : results.length > 0 ? (
          <ul className="space-y-4">
            {results.map((r) => (
              <li key={`${r.kind}-${r.id}`}>
                <Link
                  to={r.link}
                  className="group flex cursor-pointer flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span
                    className={`w-fit shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold ${
                      r.kind === "review" ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-800"
                    }`}
                  >
                    {r.typeLabel}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700 md:text-lg">
                      {r.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{r.summary}</p>
                  </div>
                  <i className="ri-arrow-right-line hidden shrink-0 text-foreground-400 transition-transform group-hover:translate-x-1 sm:block" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-8 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到相关内容</h3>
            <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者看看这些热门推荐。</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {hotKeywords.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setInput(k); submit(k); }}
                  className="cursor-pointer rounded-full bg-background-200 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-secondary-200"
                >
                  {k}
                </button>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/category/movie" className="cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
                浏览全部电影
              </Link>
              <Link to="/category/series" className="cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-400">
                浏览全部电视剧
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}